/**
 * field-error.js — 表單欄位「三件套」錯誤顯示 helper（單一真相源）
 *
 * 收斂前散落三份的各自實作為一份，行為嚴格等價：
 *   - admin-form-edit.js（最完整：setInvalid/clearInvalid/ensureErrorEl/swapDescribedBy）→ 基準。
 *   - admin-review-detail.js（setInvalid 不覆寫 HTML 既有 error 文案）→ 由 message=null 路徑相容。
 *   - clinic-hours.js（setSelectInvalid/clearSelectInvalid，作用對象是 .hours-table__seg 內的 select，
 *     無 .form-field 包裹）→ control.closest('.form-field') 為 null 時自動退化成「只切 aria-invalid」，
 *     不建 error 節點、不碰 hint / describedby，與舊 setSelectInvalid 等價。
 *
 * 設計原則（沿用 form-field atom，零新 class）：
 *   .form-field--invalid（包裹）+ [aria-invalid="true"]（control）+
 *   .form-field__error[role=alert][hidden]（錯誤節點，缺則動態補在 control 之後 / hint 之前）。
 *   報錯時 aria-describedby 由 {id}-hint 切到 {id}-error，hint 收起避免兩行擠（互斥顯示）。
 *
 * control 泛指 input / select / textarea（select 由 clinic-hours 使用，皆支援）。
 *
 * 載入：純 helper，不 auto-init。由 admin-form-edit.js / clinic-hours.js / admin-review-detail.js
 *   import 使用（三者皆在 /src/admin.js 鏈）。無 DOM 副作用，匯入即得函式。
 */

/**
 * 標記某欄為 invalid。
 * @param {HTMLElement} control input / select / textarea
 * @param {string|null} [message] 錯誤文字。
 *   - 有字串（admin-form-edit 慣例）：ensureErrorEl 取（或建）error 節點 → 寫 textContent、
 *     取消 hidden、describedby 切到 error。
 *   - null/undefined（review-detail 慣例）：不覆寫、且**不憑空建** error 節點，只揭露 HTML 已寫好的
 *     既有 error 節點（無既有節點時僅標紅框 + aria-invalid，等價 review-detail 舊 `if(errorEl)` 守衛 +
 *     selectErrorEl:null 的「標紅框 + focus 即足」行為）。
 */
function setInvalid(control, message) {
  if (!control) return;
  control.setAttribute('aria-invalid', 'true');
  const wrap = control.closest('.form-field');
  if (wrap) wrap.classList.add('form-field--invalid');

  if (message != null) {
    // 有字串：取（或建）error 節點並寫文案、顯示、describedby 指向 error。
    const errorEl = ensureErrorEl(control);
    if (!errorEl) return; // 無 .form-field 包裹（如 hours select）→ 僅 aria-invalid，等價舊 setSelectInvalid
    errorEl.textContent = message;
    errorEl.hidden = false;
    swapDescribedBy(control, true);
  } else {
    // message 省略：只揭露既有 error 節點，不憑空建（review-detail：HTML 已寫好細緻措辭 / 或無節點）。
    const errorEl = wrap ? wrap.querySelector('.form-field__error') : null;
    if (errorEl) {
      errorEl.hidden = false;
      swapDescribedBy(control, true);
    }
  }
}

/**
 * 還原某欄（移 aria-invalid / 移 .form-field--invalid / error 節點 hidden / describedby 還原指回 hint）。
 * @param {HTMLElement} control
 */
function clearInvalid(control) {
  if (!control) return;
  control.removeAttribute('aria-invalid');
  const wrap = control.closest('.form-field');
  if (wrap) wrap.classList.remove('form-field--invalid');

  const errorEl = wrap ? wrap.querySelector('.form-field__error') : null;
  if (errorEl) {
    errorEl.hidden = true;
    swapDescribedBy(control, false);
  }
}

/**
 * 取（或建）某欄的 .form-field__error 元素。HTML 多以註解保留 error 位，故動態補。
 * 無 .form-field 包裹（如 hours select）→ 回 null（呼叫端據此退化成只切 aria-invalid）。
 * @param {HTMLElement} control
 * @returns {HTMLElement|null}
 */
function ensureErrorEl(control) {
  const wrap = control.closest('.form-field');
  if (!wrap) return null;
  let errorEl = wrap.querySelector('.form-field__error');
  if (!errorEl) {
    errorEl = document.createElement('p');
    errorEl.className = 'form-field__error';
    errorEl.setAttribute('role', 'alert');
    errorEl.hidden = true;
    if (!control.id) control.id = `f-${Math.random().toString(36).slice(2, 8)}`;
    errorEl.id = `${control.id}-error`;
    // 插在 hint 前（或欄末），貼緊 control 下方。
    const hint = wrap.querySelector('.form-field__hint');
    if (hint) wrap.insertBefore(errorEl, hint);
    else wrap.appendChild(errorEl);
  }
  return errorEl;
}

/**
 * error 顯/隱時切 aria-describedby（指 error）與 hint 顯隱（互斥顯示）。
 * 首次切到 error 時記住原始 describedby（指 hint）於 dataset，清錯時還原。
 * @param {HTMLElement} control
 * @param {boolean} toError true=指向 error，false=還原指回 hint
 */
function swapDescribedBy(control, toError) {
  const wrap = control.closest('.form-field');
  const hint = wrap ? wrap.querySelector('.form-field__hint') : null;
  const errorEl = wrap ? wrap.querySelector('.form-field__error') : null;
  if (toError) {
    if (control.dataset.describedbyBase == null) {
      control.dataset.describedbyBase = control.getAttribute('aria-describedby') || '';
    }
    if (hint) hint.hidden = true;
    if (errorEl && errorEl.id) control.setAttribute('aria-describedby', errorEl.id);
  } else {
    if (hint) hint.hidden = false;
    const base = control.dataset.describedbyBase;
    if (base) control.setAttribute('aria-describedby', base);
    else control.removeAttribute('aria-describedby');
  }
}

export { setInvalid, clearInvalid, ensureErrorEl, swapDescribedBy };
